<template>
  <div>
    <v-dialog v-model="dialog" width="413" scrollable>
      <v-card id="accessPointEdit">
        <v-card-title v-if="modalMode === 'create'" class="secondary">{{$t('add-access-point')}}</v-card-title>
        <v-card-title v-if="modalMode === 'edit'" class="secondary">{{$t('edit-access-point')}}</v-card-title>
        <v-card-text>
          <v-form @submit.stop.prevent id="accessPointEditForm">
            <v-row justify="center">
              <v-col cols="11">
                <!-- name -->
                <v-text-field
                  :label="$t('name')"
                  tabindex="1"
                  :dense="true"
                  v-model="form.name"
                  outlined
                  :error-messages="nameErrors"
                  @input="$v.form.name.$touch()"
                  @blur="$v.form.name.$touch()"
                  color="secondary"
                ></v-text-field>
                <!-- mac address -->
                <v-text-field
                  :label="$t('mac-address')"
                  tabindex="2"
                  v-model="form.macAddress"
                  @keyup="formatMacAddress" 
                  maxlength="27"
                  :dense="true"
                  :error-messages="macAddressErrors"
                  :disabled="modalMode === 'edit'"
                  outlined
                  required
                  color="secondary"
                  @change="$v.form.macAddress.$touch()"
                ></v-text-field>
                <!-- Partial functionality, admin site has this UI but does not send any extra parameter to the backend 
                this is functional now if uncoment. Once the backend supports access point type, we can uncoment-->
                <!-- <v-select
                  v-model="form.type"
                  :items="accessPointsTypes"
                  :clearable="false"
                  item-text="value"
                  item-value="id"
                  :label="$t('access-point-type')"
                  tabindex="3"
                  :dense="true"
                  outlined
                  color="secondary"
                ></v-select> --> 
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-col cols="12">
            <v-btn
              v-if="modalMode === 'edit'"
              depressed
              color="secondary"
              tabindex="3"
              @click="editAccessPointFn"
              class="px-8 float-right"
            >{{$t('save')}}</v-btn>
            <v-btn
              v-if="modalMode === 'create'"
              depressed
              color="secondary"
              tabindex="3"
              @click="createAccessPointFn"
              class="px-8 float-right"
            >{{$t('add')}}</v-btn>
            <v-btn
              text
              color="secondary"
              tabindex="3"
              @click="resetForm"
              class="px-8 mr-2 float-right"
            >{{$t('cancel')}}</v-btn>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { macAddressValidator } from '../../mixins/macAddressValidator';

export default {
  data() {
    return {
      dialog: false,
      currentAccessPoint: null,
      form: {
        name: '',
        type: 'v1Slap',
        macAddress: '',
      },
      accessPointsTypes: [
        {
          id: 'v1Slap',
          value: 'v1 SLAP (Default)',
        },
        {
          id: 'symbleAP',
          value: 'Access Point OG',
        },
        {
          id: 'symbleRPi',
          value: 'Raspberry Pi (No)',
        }
      ]
    };
  },
  mixins: [validationMixin],
  validations: {
    form: {
      name: {
        required,
      },
      macAddress: {
        required,
        minLength: minLength(22),
      },
    },
  },
  props: ['modalMode', 'area'],
  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required &&
        errors.push(this.$t('error-name-required'));
      return errors;
    },
    macAddressErrors() {
      const errors = [];
      if (!this.$v.form.macAddress.$dirty) return errors;
      !this.$v.form.macAddress.required &&
        errors.push(this.$t('devices-error-mac-address-required'));
      !this.$v.form.macAddress.minLength &&
        errors.push(this.$t('devices-error-mac-address-min-length-custom', {number: 10}));
      return errors;
    },
  },
  methods: {
    ...mapActions('accessPoint', ['createAccessPoint', 'editAccessPoint']),
    openModal(accessPoint) {
      this.dialog = true;
      if (accessPoint) {
        this.currentAccessPoint = accessPoint;
        Object.assign(this.form, {
          name: accessPoint.value,
          macAddress: macAddressValidator.computed.formatMacAddress(
            accessPoint.macAddress
          ),
        });
      } else {
        Object.assign(this.form, {
          name: '',
          macAddress: '',
          type: 'v1Slap',
        });
      }
    },
    resetForm() {
      this.$v.form.$reset();
      Object.assign(this.form, {
        name: '',
        macAddress: '',
        type: 'v1Slap',
      });
      this.dialog = false;
    },
    formatMacAddress() {
      this.$set(
        this.form,
        'macAddress',
        macAddressValidator.computed.formatMacAddress(this.form.macAddress)
      );
    },
    async editAccessPointFn() {
      this.$v.$touch();
      if (!this.$v.form.$anyError && this.modalMode === 'edit') {
        const accessPoint = {
          name: this.form.name,
          siteId: this.area.site.id,
        };
        const accessPointId = this.currentAccessPoint.id;
        try {
          let resp = await this.editAccessPoint({ accessPointId, accessPoint });
          this.$toasted.show(this.$t('access-point-edited'), {
            position: 'bottom-right',
            className: ['toast-success'],
            duration: 2000,
          });
          this.$emit('changeOnAccessPoints');
          this.resetForm();
        } catch (error) {
          this.$toasted.show(error, {
            position: 'bottom-right',
            className: ['toast-error'],
            duration: 5000,
          });
        }
      }
    },
    async createAccessPointFn() {
      this.$v.$touch();
      if (!this.$v.form.$anyError && this.modalMode === 'create') {
        const accessPoint = {
          name: this.form.name,
          macAddress: this.form.macAddress.replace(/\s/g, ''),
          siteId: this.area.site.id,
        };
        try {
          const resp = await this.createAccessPoint(accessPoint);
          this.$toasted.show(this.$t('access-point-created'), {
            position: 'bottom-right',
            className: ['toast-success'],
            duration: 2000,
          });
          this.$emit('changeOnAccessPoints');
          this.resetForm();
        } catch (error) {
          this.$toasted.show(error, {
            position: 'bottom-right',
            className: ['toast-error'],
            duration: 5000,
          });
        }
      }
    },
  },
};
</script>
<style scoped>
#accessPointEditForm {
  padding-top: 2rem;
}
</style>